import React, { useState, useEffect, useRef } from 'react';
import { debtsAPI } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const CATEGORIES = ['Credit Card', 'Student Loan', 'Auto Loan', 'Personal Loan', 'Medical', 'Mortgage', 'Other'];

const defaultForm = {
  name: '', creditor: '', original_amount: '', balance: '',
  interest_rate: '', minimum_payment: '', due_date: '',
  category: '', notes: '', owner_id: ''
};

function Debts() {
  const formRef = useRef(null);
  const [debts, setDebts] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [sortOrder, setSortOrder] = useState('asc');

  useEffect(() => {
    fetchDebts();
    fetchMembers();
  }, []);

  const fetchDebts = async () => {
    try {
      const response = await debtsAPI.getAll();
      setDebts(response.data);
    } catch (error) {
      console.error('Error fetching debts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/household`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        owner_id: formData.owner_id || null,
        original_amount: formData.original_amount || formData.balance,
        interest_rate: formData.interest_rate || null,
        minimum_payment: formData.minimum_payment || null,
        due_date: formData.due_date || null,
      };
      if (editingId) {
        await debtsAPI.update(editingId, payload);
      } else {
        await debtsAPI.create(payload);
      }
      fetchDebts();
      resetForm();
    } catch (error) {
      console.error('Error saving debt:', error);
      alert('Failed to save debt');
    }
  };

  const handleEdit = (debt) => {
    setFormData({
      name: debt.name,
      creditor: debt.creditor || '',
      original_amount: debt.original_amount || '',
      balance: debt.balance,
      interest_rate: debt.interest_rate || '',
      minimum_payment: debt.minimum_payment || '',
      due_date: debt.due_date ? debt.due_date.split('T')[0] : '',
      category: debt.category || '',
      notes: debt.notes || '',
      owner_id: debt.owner_id || ''
    });
    setEditingId(debt.id);
    setShowForm(true);
    setTimeout(() => {
      if (formRef.current) {
        const top = formRef.current.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }, 50);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this debt?')) {
      try {
        await debtsAPI.delete(id);
        fetchDebts();
      } catch (error) {
        console.error('Error deleting debt:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData(defaultForm);
    setEditingId(null);
    setShowForm(false);
  };

  const sortDebts = (arr) => [...arr].sort((a, b) => {
    const diff = parseFloat(a.balance || 0) - parseFloat(b.balance || 0);
    return sortOrder === 'asc' ? diff : -diff;
  });

  // Group debts by owner
  const groupedByOwner = members.reduce((acc, member) => {
    acc[member.id] = {
      member,
      debts: sortDebts(debts.filter(d => d.owner_id === member.id))
    };
    return acc;
  }, {});

  // Also bucket any unassigned debts
  const unassignedDebts = sortDebts(debts.filter(d => !d.owner_id || !members.find(m => m.id === d.owner_id)));

  const filteredDebts = activeTab === 'all'
    ? debts
    : sortDebts(debts.filter(d => String(d.owner_id) === String(activeTab)));

  const filteredGrouped = activeTab === 'all'
    ? Object.values(groupedByOwner)
    : Object.values(groupedByOwner).filter(g => String(g.member.id) === String(activeTab));

  const totalBalance = filteredDebts.reduce((sum, d) => sum + parseFloat(d.balance || 0), 0);

  const memberBalance = (memberId) =>
    debts.filter(d => String(d.owner_id) === String(memberId))
      .reduce((sum, d) => sum + parseFloat(d.balance || 0), 0);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Debts</h2>
          <p>Track loans, credit cards, and balances by person</p>
        </div>
        <button onClick={() => { setShowForm(!showForm); if (showForm) resetForm(); }} className="btn btn-primary">
          {showForm ? 'Cancel' : '+ Add Debt'}
        </button>
      </div>

      {/* Person filter tabs */}
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '16px',
        borderBottom: '1px solid var(--border-color)', paddingBottom: '0',
        overflowX: 'auto', flexWrap: 'nowrap'
      }}>
        <button
          onClick={() => setActiveTab('all')}
          style={{
            padding: '8px 18px', fontSize: '14px', fontWeight: 600,
            background: 'none', border: 'none', cursor: 'pointer',
            borderBottom: activeTab === 'all' ? '2px solid var(--accent-coral)' : '2px solid transparent',
            color: activeTab === 'all' ? 'var(--accent-coral)' : 'var(--text-muted)',
            whiteSpace: 'nowrap', transition: 'color 0.15s'
          }}
        >
          All
        </button>
        {members.map(member => (
          <button
            key={member.id}
            onClick={() => setActiveTab(String(member.id))}
            style={{
              padding: '8px 18px', fontSize: '14px', fontWeight: 600,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === String(member.id) ? '2px solid var(--accent-coral)' : '2px solid transparent',
              color: activeTab === String(member.id) ? 'var(--accent-coral)' : 'var(--text-muted)',
              whiteSpace: 'nowrap', transition: 'color 0.15s'
            }}
          >
            {member.full_name || member.username}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      {members.length > 0 && (activeTab === 'all') && (
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
          {members.map(member => {
            const bal = memberBalance(member.id);
            const count = debts.filter(d => String(d.owner_id) === String(member.id)).length;
            return (
              <div key={member.id} style={{
                flex: '1', minWidth: '140px', padding: '12px 16px',
                background: 'var(--bg-secondary)', borderRadius: '8px',
                borderLeft: '3px solid var(--accent-coral)', cursor: 'pointer'
              }} onClick={() => setActiveTab(String(member.id))}>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  {member.full_name || member.username}
                </div>
                <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: 'var(--accent-coral)' }}>
                  ${bal.toFixed(2)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {count} debt{count !== 1 ? 's' : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="card card-table">
        {/* Add/Edit Form */}
        {showForm && (
          <div ref={formRef} className="form-section">
            <h4 style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {editingId ? 'Edit Debt' : 'New Debt'}
            </h4>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Debt Name *</label>
                  <input type="text" className="form-input" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required placeholder="e.g. Chase Sapphire, Car Loan" />
                </div>
                <div className="form-group">
                  <label className="form-label">Creditor</label>
                  <input type="text" className="form-input" value={formData.creditor}
                    onChange={(e) => setFormData({ ...formData, creditor: e.target.value })}
                    placeholder="e.g. Chase, Sallie Mae" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Current Balance *</label>
                  <input type="number" step="0.01" className="form-input" value={formData.balance}
                    onChange={(e) => setFormData({ ...formData, balance: e.target.value })}
                    required placeholder="0.00" />
                </div>
                <div className="form-group">
                  <label className="form-label">Original Amount</label>
                  <input type="number" step="0.01" className="form-input" value={formData.original_amount}
                    onChange={(e) => setFormData({ ...formData, original_amount: e.target.value })}
                    placeholder="0.00" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Interest Rate (%)</label>
                  <input type="number" step="0.01" className="form-input" value={formData.interest_rate}
                    onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                    placeholder="e.g. 19.99" />
                </div>
                <div className="form-group">
                  <label className="form-label">Min. Payment</label>
                  <input type="number" step="0.01" className="form-input" value={formData.minimum_payment}
                    onChange={(e) => setFormData({ ...formData, minimum_payment: e.target.value })}
                    placeholder="0.00" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select className="form-select" value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}>
                    <option value="">— Select —</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input type="date" className="form-input" value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Assign To *</label>
                  <select className="form-select" value={formData.owner_id}
                    onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })} required>
                    <option value="">— Select person —</option>
                    {members.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.full_name || member.username}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <input type="text" className="form-input" value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '16px' }}>
                  <div className="flex gap-1">
                    <button type="submit" className="btn btn-success">
                      {editingId ? 'Update' : 'Add'}
                    </button>
                    <button type="button" onClick={resetForm} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Total bar + sort toggle */}
        <div className="total-display" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span className="total-label">
              {activeTab === 'all' ? 'Total Balance:' : `${members.find(m => String(m.id) === activeTab)?.full_name || 'Balance'}:`}
            </span>
            <span className="total-amount danger">${totalBalance.toFixed(2)}</span>
          </div>
          <button
            onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
            className="btn btn-secondary btn-sm"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              {sortOrder === 'asc'
                ? <><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></>
                : <><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></>}
            </svg>
            {sortOrder === 'asc' ? 'Lowest first' : 'Highest first'}
          </button>
        </div>

        {/* Debt table grouped by person */}
        {filteredDebts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💳</div>
            <p className="empty-state-text">No debts yet</p>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">Add Your First Debt</button>
          </div>
        ) : (
          <div className="tracker-table-wrap">
            <table className="tracker-table">
              <thead>
                <tr>
                  <th className="tracker-th col-name">Name</th>
                  <th className="tracker-th col-name">Creditor</th>
                  <th className="tracker-th col-amount">Balance</th>
                  <th className="tracker-th col-amount">Original</th>
                  <th className="tracker-th col-category">Category</th>
                  <th className="tracker-th col-category">Rate</th>
                  <th className="tracker-th col-amount">Min. Pay</th>
                  <th className="tracker-th col-due">Due Date</th>
                  <th className="tracker-th col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {activeTab === 'all' ? (
                  <>
                    {filteredGrouped.map(({ member, debts: memberDebts }) => {
                      if (memberDebts.length === 0) return null;
                      const personTotal = memberDebts.reduce((s, d) => s + parseFloat(d.balance || 0), 0);
                      return (
                        <React.Fragment key={member.id}>
                          {/* Person header row */}
                          <tr>
                            <td colSpan={9} style={{
                              padding: '10px 16px',
                              background: 'var(--bg-tertiary, var(--bg-secondary))',
                              borderTop: '2px solid var(--accent-coral)',
                              borderBottom: '1px solid var(--border-color)'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{
                                    width: '28px', height: '28px', borderRadius: '50%',
                                    background: 'var(--accent-coral)', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontSize: '13px', fontWeight: 700, color: '#fff', flexShrink: 0
                                  }}>
                                    {(member.full_name || member.username).charAt(0).toUpperCase()}
                                  </div>
                                  <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>
                                    {member.full_name || member.username}
                                  </span>
                                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                    {memberDebts.length} debt{memberDebts.length !== 1 ? 's' : ''}
                                  </span>
                                </div>
                                <span style={{
                                  fontFamily: "'JetBrains Mono', monospace", fontWeight: 700,
                                  fontSize: '15px', color: 'var(--accent-coral)'
                                }}>
                                  ${personTotal.toFixed(2)}
                                </span>
                              </div>
                            </td>
                          </tr>
                          {memberDebts.map(debt => (
                            <DebtRow key={debt.id} debt={debt} onEdit={handleEdit} onDelete={handleDelete} />
                          ))}
                        </React.Fragment>
                      );
                    })}
                    {unassignedDebts.length > 0 && (
                      <React.Fragment>
                        <tr>
                          <td colSpan={9} style={{
                            padding: '10px 16px',
                            background: 'var(--bg-tertiary, var(--bg-secondary))',
                            borderTop: '2px solid var(--border-color)',
                            borderBottom: '1px solid var(--border-color)'
                          }}>
                            <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-muted)' }}>
                              Unassigned
                            </span>
                          </td>
                        </tr>
                        {unassignedDebts.map(debt => (
                          <DebtRow key={debt.id} debt={debt} onEdit={handleEdit} onDelete={handleDelete} />
                        ))}
                      </React.Fragment>
                    )}
                  </>
                ) : (
                  filteredDebts.map(debt => (
                    <DebtRow key={debt.id} debt={debt} onEdit={handleEdit} onDelete={handleDelete} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function DebtRow({ debt, onEdit, onDelete }) {
  const balance = parseFloat(debt.balance || 0);
  const original = parseFloat(debt.original_amount || debt.balance || 0);
  const progress = original > 0 ? Math.max(0, Math.min(100, ((original - balance) / original) * 100)) : 0;

  return (
    <tr className="tracker-row">
      <td className="tracker-td col-name tracker-name">
        <div>{debt.name}</div>
        {original > 0 && balance < original && (
          <div style={{ marginTop: '4px' }}>
            <div style={{
              height: '4px', borderRadius: '2px', background: 'var(--border-color)',
              overflow: 'hidden', width: '100%', maxWidth: '120px'
            }}>
              <div style={{
                height: '100%', width: `${progress}%`,
                background: progress > 75 ? 'var(--accent-green, #22c55e)' : 'var(--accent-coral)',
                borderRadius: '2px', transition: 'width 0.3s'
              }} />
            </div>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{progress.toFixed(0)}% paid off</span>
          </div>
        )}
      </td>
      <td className="tracker-td col-name">
        {debt.creditor
          ? <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{debt.creditor}</span>
          : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
      </td>
      <td className="tracker-td col-amount tracker-mono" style={{ color: 'var(--accent-coral)', fontWeight: 700 }}>
        ${balance.toFixed(2)}
      </td>
      <td className="tracker-td col-amount tracker-mono">
        {original > 0
          ? <span style={{ color: 'var(--text-muted)' }}>${original.toFixed(2)}</span>
          : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
      </td>
      <td className="tracker-td col-category">
        {debt.category
          ? <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{debt.category}</span>
          : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
      </td>
      <td className="tracker-td col-category">
        {debt.interest_rate
          ? <span className="badge badge-warning">{parseFloat(debt.interest_rate).toFixed(2)}%</span>
          : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
      </td>
      <td className="tracker-td col-amount tracker-mono">
        {debt.minimum_payment
          ? `$${parseFloat(debt.minimum_payment).toFixed(2)}`
          : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
      </td>
      <td className="tracker-td col-due">
        {debt.due_date
          ? new Date(debt.due_date).toLocaleDateString()
          : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
      </td>
      <td className="tracker-td col-actions">
        <div className="list-item-actions">
          <button onClick={() => onEdit(debt)} className="btn btn-secondary btn-sm">Edit</button>
          <button onClick={() => onDelete(debt.id)} className="btn btn-danger btn-sm">Delete</button>
        </div>
      </td>
    </tr>
  );
}

export default Debts;
